// MODULES
import React from "react";

// HELPERS
import {createClassName, getPropertiesByPrefix, parseDraftDataPropsToFieldState, delay} from "@helpers/utils";
import fulfillmentCheck from "./AccountChooser.fulfillment";
import DefaultFields from "../../../components/DefaultFields/DefaultFields";
import PropTypes from "prop-types";
import Select from "../../../../../components/Select/Select";
import OptionsList from "../../../components/OptionsList/OptionsList";
import EditableField from "../../../components/EditableField/EditableField";
import Checkbox from "../../../../../components/Checkbox/Checkbox";
import Validator from "../../../../../classes/Validator";
import * as CobHelpers from "../../../Cob.helpers";

export default class AccountChooser extends React.PureComponent
{
    static propTypes = {
        variables: PropTypes.object.isRequired
    };

    static companyExclusive = false;
    static privateExclusive = false;

    static fields = _getFields();
    state = _getInitialState.call(this);

    componentDidUpdate(prevProps, prevState)
    {
        const {props, state} = this;
        const {id, onChange} = props;

        if (prevState.fields !== state.fields)
        {
            onChange(id, {...state}, fulfillmentCheck(props, state));
        }

        if (prevProps.draftData !== props.draftData)
        {
            if (props.draftData)
            {
                // get the draftData props specific to this section only
                const draftDataProps = getPropertiesByPrefix(props.draftData, ["futur_insured_", "checkbox_"]);
                // parse the draftData props into state.fields
                const newFieldsState = parseDraftDataPropsToFieldState(this.state.fields, draftDataProps);

                this.setState(
                    {
                        fields: {
                            ...newFieldsState
                        }
                    },
                    async () =>
                    {
                        await delay(200);
                        onChange(id, {...this.state}, fulfillmentCheck(this.props, this.state));
                    }
                );
            }
        }
    }

    render()
    {
        const {props, state} = this;
        const {fields} = state;
        const {title, availableBanks} = props;

        const classes = createClassName("Cob-Fees", {
            "Cob-View": true,
            "fulfilled": state.isFulfilled
        });


        const accountsForDisplay = [];

        for (const key in fields.accounts)
        {
            const accountValue = fields.accounts[key];
            const accountFormatted = accountValue.substring(0, 4) + " " + accountValue.substring(4, accountValue.length);
            accountsForDisplay.push({
                label: accountFormatted,
                value: accountValue
            });
        }

        // Set up the dropdown with selectable banks.
        let availableBankOptions = [];
        for (const bankName in availableBanks)
        {
            if (bankName === "length" || !availableBanks.hasOwnProperty(bankName))
            {
                continue;
            }
            availableBankOptions.push({
                label: bankName,
                value: bankName
            });
        }

        availableBankOptions.push({
            label: "Övriga",
            value: "-"
        });

        let selectedBankIndex = -1;

        if (state.fields.futur_new_bank && state.fields.futur_new_bank.length > 0)
        {
            let bankFoundInList = false;
            for (const bankName in availableBanks)
            {
                if (bankName === "length" || !availableBanks.hasOwnProperty(bankName))
                {
                    continue;
                }
                selectedBankIndex++;
                if (bankName === state.fields.futur_new_bank)
                {
                    bankFoundInList = true;
                    break;
                }
            }

            if (!bankFoundInList && state.fields.futur_new_bank === "-")
            {
                selectedBankIndex++;
            }
        }

        return (
            <div className={classes}>
                <DefaultFields fields={this.constructor.fields} />

                <div className="Cob-Fields">
                    <h2>{title}</h2>

                    <p style={{marginTop: "0"}}>
                        OBS! Utbetalning kan endast ske till försäkringstagarens konto (transaktionskonto).<br />
                        Vid företagsägd kapitalförsäkring kan utbetalning endast ske till företagets konto.
                    </p>

                    <OptionsList
                        type="radio"
                        orientation="horizontal"
                        name="radio_account_type"
                        description="Välj utbetalningskonto"
                        options={fields.radio_account_type.options}
                        onChange={this._onChangeRadio}
                        style={{marginBottom: "10px"}}
                    />

                    {fields.radio_account_type.options[0].checked && (
                        <React.Fragment>
                            <Select name="futur_withdrawal_account"
                                    label="Föranmälda konton"
                                    style={{marginRight: "10px"}}
                                    selectedIndex={fields.selectedAccount ? (accountsForDisplay.map(c => c.value).indexOf(fields.selectedAccount)) : -1}
                                    options={accountsForDisplay}
                                    onChange={this._onSelectAccount}
                            />
                        </React.Fragment>
                    )}

                    {fields.radio_account_type.options[1].checked && (
                        <React.Fragment>
                            {(fields.accounts.length === 0) && (
                                <p style={{marginTop: "0", color: "red"}}>
                                    Konto saknas hos Futur, nytt uttagskonto måste anmälas.
                                </p>
                            )}

                            <div style={{width: "100%", flexDirection: "row", margin: "15px 0 5px 0", alignItems: "flex-start"}}>

                                <div style={{width: "45%", marginRight: "10px", flexDirection: "column"}}>
                                    <Select
                                        name="futur_new_bank"
                                        label="Bank"
                                        selectedIndex={selectedBankIndex}
                                        options={availableBankOptions}
                                        onChange={this._onSelectBankChange}
                                    />

                                    {fields.futur_new_bank === "-" && (
                                        <React.Fragment>
                                            <EditableField
                                                name="futur_new_bank_custom"
                                                label="Bankens namn"
                                                value={fields.futur_new_bank_custom || ""}
                                                valid={fields.futur_new_bank_custom.length > 0 || null}
                                                onChange={this._onFieldChange}
                                                onBlur={this._onFieldBlur}
                                                style={{marginTop: "10px"}}
                                            />
                                            <input type="hidden" name="futur_new_bank" value={fields.futur_new_bank_custom} />
                                        </React.Fragment>
                                    )}

                                    {fields.futur_new_bank !== "-" && (
                                        <input type="hidden" name="futur_new_bank_custom" value="" />
                                    )}
                                </div>

                                <div style={{width: "45%"}}>
                                    <EditableField
                                        name="futur_new_holder"
                                        label="Kontoinnehavare"
                                        value={fields.futur_new_holder || ""}
                                        valid={fields.futur_new_holder.length > 0 || null}
                                        onChange={this._onTextFieldChange}
                                        onBlur={this._onFieldBlur}
                                    />
                                </div>
                            </div>

                            {fields.futur_new_bank === "Swedbank" && (
                                <p style={{
                                    background: "#FFFFD5",
                                    padding: "10px",
                                    fontSize: "12px",
                                    margin: "10px 0",
                                    maxWidth: "600px"
                                }}>
                                    Det förekommer att Swedbank har en femte siffra i clearingnumret. Vänligen ange
                                    enbart de fyra första innan bindestrecket. Exempel: 8214-9 ska anges som 8214.
                                </p>
                            )}


                            <div style={{width: "100%", flexDirection: "row", margin: "5px 0", flexWrap: "wrap"}}>
                                <EditableField
                                    name="futur_new_clearing"
                                    style={{width: "45%", marginRight: "10px"}}
                                    label="Clearingnummer"
                                    value={fields.futur_new_clearing || ""}
                                    valid={fields.futur_new_clearing.length > 0 ? (fields.futur_new_clearing.length > 3 ? Validator.validateClearingNumber(fields.futur_new_clearing, fields.futur_new_bank, state.availableBanks) : false) : null}
                                    onChange={this._onClearingFieldChange}
                                    onBlur={this._onFieldBlur}
                                />

                                <EditableField
                                    name="futur_new_account"
                                    style={{width: "45%"}}
                                    label="Kontonummer"
                                    value={fields.futur_new_account || ""}
                                    valid={fields.futur_new_account.length > 0 ? (!!window.kontonummer(fields.futur_new_clearing + ", " + fields.futur_new_account)) : null}
                                    onChange={this._onAccountFieldChange}
                                    onBlur={this._onFieldBlur}
                                />

                                {fields.shouldValidateBankAccount && (
                                    <Checkbox
                                        label="Tillåt kontonummer som inte validerar"
                                        checked={fields.skipBankAccountValidation}
                                        style={{width: "100%", justifyContent: "flex-start", marginTop: "10px"}}
                                        onChange={(checked) =>
                                        {
                                            this.setState({
                                                fields: {
                                                    ...this.state.fields,
                                                    skipBankAccountValidation: checked
                                                }
                                            });
                                        }}
                                    />
                                )}

                                <input type="hidden" name="futur_new_clearing" value={fields.futur_new_clearing || ""} />
                            </div>

                            <p style={{marginTop: "10px"}}>
                                Konton som anmälts före kl 07 hinner kontrolleras av Futur till ca kl 13 samma dag.<br />
                                Dokumentationen kommer att skickas till kund automatiskt efter att konto har godkänts.
                            </p>
                        </React.Fragment>
                    )}

                </div>
            </div>
        );
    }

    _onFieldChange = (name, value) =>
    {
        CobHelpers.onViewFieldChange.call(this, name, value);
    };

    _onFieldBlur = (name) =>
    {
        if (name === "futur_new_clearing")
        {
            CobHelpers.onViewFieldBlur.call(this, name, null, true);
        }
        else
        {
            CobHelpers.onViewFieldBlur.call(this, name);
        }
    };

    _onSelectBankChange = (option) =>
    {
        let shouldValidateAccountNumber = false;
        const availableBanks = this.props.availableBanks;
        for (const bankName in availableBanks)
        {
            if (bankName === "length" || !availableBanks.hasOwnProperty(bankName))
            {
                continue;
            }

            if (option.value === bankName)
            {
                const bankInfo = availableBanks[bankName];
                if (bankInfo.length > 0)
                {
                    if (bankInfo[0]["validationEnabled"] === "1")
                    {
                        shouldValidateAccountNumber = true;
                    }
                }
                break;
            }
        }

        this.setState({
            fields: {
                ...this.state.fields,
                futur_new_bank: option.value,
                shouldValidateBankAccount: shouldValidateAccountNumber
            }
        });
    };

    _onSelectAccount = (option) =>
    {
        let selected = this.state.fields.selectedAccount;
        for (const key in this.state.fields.accounts)
        {
            if (this.state.fields.accounts[key] === option.value)
            {
                selected = this.state.fields.accounts[key];
                break;
            }
        }

        this.setState({
            fields: {
                ...this.state.fields,
                selectedAccount: selected
            }
        });
    };

    _onChangeRadio = ({type, name}, option, index) =>
    {
        name = name || option.name;
        const newOptions = [...this.state.fields[name].options].map((o, i) =>
        {
            if (i === index)
            {
                return option;
            }
            else
            {
                return {...o, checked: type === "radio" ? false : o.checked};
            }
        });


        this.setState({
            fields: {
                ...this.state.fields,
                selectedAccount: null,
                [name]: {
                    options: newOptions
                }
            }
        }, () => fulfillmentCheck(this.props, this.state));
    };

    _onAccountFieldChange = (name, value) =>
    {
        value = value.replace("-", ""); // Remove dashes
        value = value.replace(" ", ""); // Remove Spaces
        CobHelpers.onViewFieldChange.call(this, name, value);
    };

    _onTextFieldChange = (name, value) =>
    {
        CobHelpers.onViewFieldChange.call(this, name, value);
    };

    _onClearingFieldChange = (name, value) =>
    {
        // Remove all non-numbers
        value = value.replace(/[^0-9]+/g, "");

        // Limit clearing numbers to 4 numbers, we'll show a message that says to enter only first four for the
        // banks with longer numbers
        if (value.length >= 4)
        {
            value = value.substring(0, 4);
        }

        let valid = (this.state.fields.futur_new_bank === "-") ?
            true :
            Validator.validateClearingNumber(value, this.state.fields.futur_new_bank, this.props.availableBanks);

        CobHelpers.onViewFieldChange.call(this, name, value, valid);
    };

    _fieldValidator = (name, value) =>
    {
        if (name === "futur_new_account")
        {
            /**
             * For the banks where we know the validation rules, check if the number should be validated.
             */
            let shouldValidateAccountNumber = false;
            const availableBanks = this.props.availableBanks;
            for (const bankName in availableBanks)
            {
                if (bankName === "length" || !availableBanks.hasOwnProperty(bankName))
                {
                    continue;
                }

                if (this.state.fields.futur_new_bank === bankName)
                {
                    const bankInfo = availableBanks[bankName];
                    if (bankInfo.length > 0)
                    {
                        if (bankInfo[0]["validationEnabled"] === "1")
                        {
                            shouldValidateAccountNumber = true;
                        }
                    }
                    break;
                }
            }

            return this._isValidBankAccountNo(shouldValidateAccountNumber, value, this.state.fields.futur_new_clearing);
        }

        return value.length > 0;
    };

    _isValidBankAccountNo(doAdvancedValidation, accountNumber, clearingNumber)
    {
        if (accountNumber.length === 0)
        {
            return false;
        }

        if (doAdvancedValidation)
        {
            let bankNoToValidate = clearingNumber + ", " + accountNumber;
            let kontonummer = window.kontonummer(bankNoToValidate);
            return kontonummer !== false;
        }

        const replacedValue = accountNumber.split(" ").join("");
        return replacedValue.length > 5 && !isNaN(replacedValue);
    }
}

function _getFields()
{
    return [];
}

function _getInitialState()
{
    if (this.props.prevState)
    {
        return {...this.props.prevState};
    }

    const accounts = this.props.variables.accounts ? this.props.variables.accounts : [];

    return {
        validFields: {},
        availableBanks: this.props.availableBanks,

        fields: {
            radio_account_type: {
                options: [
                    {label: "Föranmälda konton", value: "approved", checked: (accounts.length > 0), disabled: (accounts.length === 0)},
                    {label: "Anmäl nytt konto till Futur", value: "new", checked: (accounts.length === 0)}
                ]
            },
            accounts: accounts,
            selectedAccount: null,

            skipBankAccountValidation: false,
            futur_new_holder: "",
            futur_new_bank: "",
            futur_new_bank_custom: "",
            futur_new_clearing: "",
            futur_new_account: ""
        }
    };
}


// WEBPACK FOOTER //
// ./src/routes/Cob/views/Futur/Withdrawal/AccountChooser.js